import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtCurrency } from "@/lib/format";
import { SelectField, useAssignees } from "@/lib/crm";
import { fmtMonth } from "@/lib/sales";
import { STATUS } from "@/design/tokens";
import { PageHeader, Panel, SectionLabel } from "@/components/common/ui-kit";
import { Delta, PeriodFilter, periodLabel, usePeriod } from "@/components/common/PeriodFilter";

const Kpi = ({ label, value, sub, delta, testId }) => (
  <div data-testid={testId}><p className="text-xs text-slate-500">{label}</p><p className="mt-1 font-display text-2xl font-bold tracking-tight text-ink">{value}</p>{sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}{delta && <p className="mt-0.5">{delta}</p>}</div>
);

const Bars = ({ rows, label, testId, tone }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return rows.length === 0 ? <p className="mt-3 text-sm text-slate-500">Nessun dato.</p> : (
    <ul className="mt-4 space-y-3" data-testid={testId}>{rows.map((r) => (
      <li key={r.key}>
        <div className="flex items-baseline justify-between gap-3 text-sm"><span className={`truncate ${r.overdue ? "font-semibold text-red-600" : "text-ink"}`}>{label(r)}</span>
          <span className="shrink-0 text-xs text-slate-500">{r.count} · {fmtCurrency(r.value)} · <span className="font-semibold text-ink">{fmtCurrency(r.weighted)}</span></span></div>
        <div className="mt-1.5 h-2 rounded-full bg-slate-100"><div className="relative h-2 rounded-full bg-brand-100" style={{ width: `${(r.value / max) * 100}%` }}>
          <div className={`absolute inset-y-0 left-0 rounded-full ${tone ? tone(r) : "bg-brand-500"}`} style={{ width: `${r.value ? (r.weighted / r.value) * 100 : 0}%` }} /></div></div>
      </li>))}</ul>
  );
};

export default function ForecastPage() {
  const users = useAssignees();
  const [owner, setOwner] = useState("");
  const [period, setPeriod] = usePeriod("onsuite.forecast.period");
  const [d, setD] = useState(null);
  useEffect(() => {
    const params = owner ? { owner_id: owner } : {};
    Promise.all(["summary", "monthly", "by-owner", "by-stage"].map((k) => api.get(`/sales/forecast/${k}`, { params: k === "by-owner" ? {} : k === "summary" ? { ...params, period } : params }).then((r) => r.data)))
      .then(([summary, monthly, byOwner, byStage]) => setD({ summary, monthly, byOwner, byStage })).catch((e) => toast.error(errorMessage(e)));
  }, [owner, period]);
  const c = d?.summary.current, p = d?.summary.previous;

  return (
    <div data-testid="forecast-page">
      <PageHeader overline="Sales" title="Forecast" description="Valore della pipeline e previsione ponderata (valore × probabilità)."
        actions={<div className="flex flex-wrap items-center gap-3"><PeriodFilter value={period} onChange={setPeriod} testId="forecast-period" />
          <SelectField value={owner} onChange={setOwner} options={users} allowNone="Tutti i commerciali" testId="forecast-filter-owner" className="w-56" /></div>} />
      {d && <>
        <Panel className="mb-6 grid grid-cols-2 gap-6 p-5 lg:grid-cols-5">
          <Kpi label="Valore pipeline" value={fmtCurrency(d.summary.pipeline_value)} sub={`${d.summary.open_count} opportunità aperte`} testId="forecast-pipeline" />
          <Kpi label="Valore pesato" value={fmtCurrency(d.summary.weighted_value)} testId="forecast-weighted" />
          <Kpi label="Chiusure previste nel periodo" value={fmtCurrency(c.closing.value)} sub={`${c.closing.count} opp. aperte · pesato ${fmtCurrency(c.closing.weighted)}`}
            delta={<Delta cur={c.closing.value} prev={p.closing.value} period={period} testId="forecast-closing-delta" />} testId="forecast-closing" />
          <Kpi label="Vinto nel periodo" value={fmtCurrency(c.won.value)} sub={`${c.won.count} vinte · ${c.lost} perse`}
            delta={<Delta cur={c.won.value} prev={p.won.value} period={period} testId="forecast-won-delta" />} testId="forecast-won" />
          <Kpi label="Tasso di successo" value={c.win_rate === null ? "—" : `${c.win_rate}%`} sub="sulle chiuse del periodo"
            delta={<Delta cur={c.win_rate} prev={p.win_rate} period={period} points testId="forecast-winrate-delta" />} testId="forecast-winrate" />
        </Panel>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Panel className="p-5"><SectionLabel>Per mese di chiusura prevista</SectionLabel>
            <Bars rows={d.monthly.items} label={(r) => `${fmtMonth(r.key)}${r.overdue ? " · data superata" : ""}`} testId="forecast-monthly" /></Panel>
          <Panel className="p-5"><SectionLabel>Per fase</SectionLabel>
            <Bars rows={d.byStage.items} label={(r) => `${r.name} (${r.probability}%)`} tone={(r) => STATUS[r.tone]?.dot || "bg-brand-500"} testId="forecast-by-stage" /></Panel>
          {!owner && <Panel className="p-5 lg:col-span-2"><SectionLabel>Per commerciale (opportunità aperte)</SectionLabel>
            <Bars rows={d.byOwner.items} label={(r) => r.name} testId="forecast-by-owner" /></Panel>}
        </div>
        <p className="mt-4 text-xs text-slate-500">Periodo «{periodLabel(period)}» applicato a chiusure previste, vinto e tasso di successo; pipeline e grafici mostrano le opportunità aperte ad oggi. Barra chiara = valore; barra piena = valore pesato. I dati rispettano il tuo perimetro di visibilità.</p>
      </>}
    </div>
  );
}
